import React from 'react'
import PropTypes from 'prop-types'
import LazyLoad from 'vanilla-lazyload'
import inView from 'in-view'
import TaxonCard from './TaxonCard'
import { ThemeOverride, Header } from '../core'
import { helpers, prismicHelpers } from '../../helpers'

/**
* TaxonList component
*/
class TaxonList extends React.Component {
  constructor(props) {
    super(props)
    this.uid = helpers.generateID('taxonList')
    this.selector = `#${this.uid}`

    this.state = {
      taxons: props.data.taxons,
      loading: true,
      error: false,
    }

    this.getTaxonData = this.getTaxonData.bind(this)
    this.replaceImageWithFallback = this.replaceImageWithFallback.bind(this)
    this.renderTaxons = this.renderTaxons.bind(this)
  }

  /**
  * Fetches taxon data once the component is in the viewport.
  */
  componentDidMount() {
    inView(this.selector)
      .once('enter', this.getTaxonData)
  }

  /**
  * Adds lazy loading of taxon images.
  */
  componentDidUpdate() {
    if (this.state.error || this.state.loading || !this.state.taxons.length) {
      return
    }
    new LazyLoad({
      container: document.querySelector(`#${this.uid}`),
      elements_selector: `#${this.uid} .is-lazy`,
      threshold: 100,
      data_src: 'original',
      data_srcset: 'original-set',
      callback_error: this.replaceImageWithFallback,
    })
  }

  /**
  * `on_error` callback for image lazy load on taxon cards. Replaces the given
  * `image` dom element with a fallback text replacement with the `image`'s '
  * `fallbackText` data attribute value.
  *
  * @param image {element} failed image element that needs to be replaced.
  */
  replaceImageWithFallback(image) {
    const fallbackText = document.createElement('span')

    if (!image || !('fallbackText' in image.dataset)) {
      return
    }
    fallbackText.className = 'taxon-fallback'
    fallbackText.appendChild(document.createTextNode(image.dataset.fallbackText))
    image.replaceWith(fallbackText)
  }

  /**
  * Fetches and formats a list of taxons from the server.
  */
  getTaxonData() {
    // Grab a list of just product skus.
    const path = `/api/prismic/taxon_list/${this.props.data.id}`

    debug.data('GET', path)
    debug.performanceStart(path)
    $.getJSON(path)
      .done((data) => {
        debug.performanceEnd(path)
        debug.data('SUCCESS', data)
        // Combine prismic data with product data
        const taxons = this.props.data.taxons
          .filter(taxon => prismicHelpers.userHasPermission(taxon))
          .map((taxon) => {
            for (let i = 0; i < data.length; i++) {
              if (taxon.taxonId === String(data[i].id)) {
                return Object.assign({}, data[i], taxon)
              }
            }

            return taxon
          })

        this.setState({
          taxons,
          loading: false,
        })
      })
      .fail((data) => {
        console.error(data)
        this.setState({
          error: true,
          loading: false,
        })
      })
  }

  /**
  * Renders individual Product components.
  */
  renderTaxons() {
    return this.state.taxons
      .map((taxon, idx) => {
        const viewableClass = prismicHelpers.formatViewClasses(taxon)

        return (
          <li key={idx} className={viewableClass}>
            <TaxonCard
              taxon={taxon}
              loading={this.state.loading}
              listName={this.props.data.uid}
            />
          </li>
        )
      })
  }

  render() {
    const viewableClass = prismicHelpers.formatViewClasses(this.props.data)

    if (!this.state.taxons.length || !prismicHelpers.userHasPermission(this.props.data) || (!this.state.loading && this.state.error)) {
      return false
    }

    return (
      <div id={this.uid} className={`taxon-list ${viewableClass}`}>
        <ThemeOverride
          selector={`#${this.uid}`}
          textColor={this.props.data.primaryColor}
          backgroundColor={this.props.data.primaryColor}
        />
        <Header
          headerType="default"
          headerTitle={this.props.data.title}
          headerLink={{ text: this.props.data.linkText, label: this.props.data.linkLabel, url: this.props.data.link }}
          metaData={{
            name: this.props.data.listName,
          }}
        />
        <ul className="taxon-list-items">
          { this.renderTaxons() }
        </ul>
      </div>
    )
  }
}

TaxonList.defaultProps = {
  data: {
    taxons: [],
    uid: '',
  },

}

TaxonList.propTypes = {
  data: PropTypes.object,
}

export default TaxonList
